import { useState, useMemo, useRef } from 'react';
import { IoChevronDown, IoChevronForward, IoChevronBack } from 'react-icons/io5';
import { BiSearch } from 'react-icons/bi';
import styles from './DataTable.module.css';

function DataTable({
  columns,
  rows,
  filterOptions,
  filterValue,
  onFilterChange,
  searchable = false,
  searchPlaceholder = 'Search...',
  paginated = false,
  pageSize = 10,
  // Server-side pagination: pass all three to activate
  page: externalPage,
  totalPages: externalTotalPages,
  onPageChange,
  onSearchChange,
  loadingRows = false,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [clientPage, setClientPage] = useState(1);
  const debounceRef = useRef(null);

  const serverMode   = paginated && externalPage != null && onPageChange != null;
  const currentPage  = serverMode ? externalPage : clientPage;
  const goToPage     = serverMode ? onPageChange : setClientPage;

  const filteredRows = useMemo(() => {
    if (serverMode || !searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, searchQuery, serverMode]);

  const totalPages = serverMode
    ? (externalTotalPages ?? 1)
    : (paginated ? Math.ceil(filteredRows.length / pageSize) : 1);

  const visibleRows = serverMode
    ? filteredRows
    : (paginated ? filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize) : filteredRows);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (serverMode) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onPageChange?.(1);
        onSearchChange?.(val);
      }, 350);
    } else {
      setClientPage(1);
    }
  };

  const renderPageNumbers = () => {
    const WINDOW = 10;
    const half   = Math.floor(WINDOW / 2);
    let start    = Math.max(1, currentPage - half);
    let end      = Math.min(totalPages, start + WINDOW - 1);
    if (end - start + 1 < WINDOW) start = Math.max(1, end - WINDOW + 1);

    const pages = [];
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className={styles.card}>
      {(filterOptions || searchable) && (
        <div className={styles.header}>
          {searchable && (
            <div className={styles.search}>
              <BiSearch size={14} className={styles.searchIcon} />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={handleSearch}
                className={styles.searchInput}
              />
            </div>
          )}
          {filterOptions && (
            <div className={styles.filter}>
              <select value={filterValue} onChange={(e) => onFilterChange?.(e.target.value)}>
                {filterOptions.map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </select>
              <IoChevronDown size={12} />
            </div>
          )}
        </div>
      )}

      <div className={styles.tableWrapper}>
        {loadingRows && (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner} />
          </div>
        )}
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} className={col.align === 'right' ? styles.thRight : ''}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => (
              <tr key={i}>
                {columns.map((col) => (
                  <td key={col.key}>
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {paginated && totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageArrow}
            onClick={() => goToPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <IoChevronBack size={16} />
          </button>

          <div className={styles.pageNumbers}>
            {renderPageNumbers().map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className={styles.ellipsis}>...</span>
              ) : (
                <button
                  key={p}
                  className={`${styles.pageNum} ${currentPage === p ? styles.pageNumActive : ''}`}
                  onClick={() => goToPage(p)}
                >
                  {p}
                </button>
              )
            )}
          </div>

          <button
            className={`${styles.pageArrow} ${styles.pageArrowNext}`}
            onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <IoChevronForward size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default DataTable;
